import api from './api';

export interface ExtractionCostEstimate {
  total_documents: number;
  regime: 'A' | 'B';
  estimated_llm_calls: number;
  estimated_cost_cents: number;
  currency: string;
  model_id: string;
  model_name: string | null;
  regime_description: string;
}

export interface ExtractionLaunchRequest {
  kb_id: string;
  intent: string;
  target_context?: string;
  destination_kb_id?: string;
  must_have_keywords: string[];
  forced_regime?: 'A' | 'B';
}

export interface ExtractionLaunchResponse {
  transcript_id: string;
  regime: 'A' | 'B';
  kb_size: number;
}

export const EXTRACTION_TARGET_CONTEXTS = [
  { value: 'state_of_the_art', label: 'State of the Art' },
  { value: 'fto', label: 'Freedom to Operate' },
  { value: 'patentability', label: 'Patentability' },
  { value: 'our_solution', label: 'Our Solution' },
  { value: 'industrialization', label: 'Industrialization' },
  { value: 'patent_application', label: 'Patent Application' },
] as const;

export interface KBExtractionLaunchRequest {
  intent: string;
  destination_kb_id?: string;
  must_have_keywords: string[];
  forced_regime?: 'A' | 'B';
}

export interface TranscriptDocumentHit {
  doc_id: string;
  title: string;
  score: number;
  justification: string;
  relevance_explanation?: string;
}

export interface TranscriptFinalResult {
  type: string;
  regime: string;
  kb_id: string;
  intent: string;
  document_hits: TranscriptDocumentHit[];
  documents_returned: number;
}

export interface Transcript {
  transcript_id: string;
  status: 'running' | 'completed' | 'error';
  final_result?: TranscriptFinalResult;
  error_message?: string;
}

export const extractionService = {
  async estimateCost(subProjectId: string, kbId: string, forcedRegime?: 'A' | 'B'): Promise<ExtractionCostEstimate> {
    const res = await api.post(
      `/sub-projects/${subProjectId}/extraction/estimate`,
      { kb_id: kbId, forced_regime: forcedRegime }
    );
    return res.data;
  },

  async launch(subProjectId: string, data: ExtractionLaunchRequest): Promise<ExtractionLaunchResponse> {
    const res = await api.post(
      `/sub-projects/${subProjectId}/extraction`,
      data
    );
    return res.data;
  },

  async estimateCostForKb(kbId: string, forcedRegime?: 'A' | 'B'): Promise<ExtractionCostEstimate> {
    const res = await api.post(
      `/knowledge-bases/${kbId}/extraction/estimate`,
      { forced_regime: forcedRegime }
    );
    return res.data;
  },

  async launchForKb(kbId: string, data: KBExtractionLaunchRequest): Promise<ExtractionLaunchResponse> {
    const res = await api.post(
      `/knowledge-bases/${kbId}/extraction`,
      data
    );
    return res.data;
  },

  async getTranscript(transcriptId: string): Promise<Transcript> {
    const res = await api.get(`/transcripts/${transcriptId}`);
    return res.data;
  },

  async pollTranscriptUntilDone(
    transcriptId: string,
    onDone: (result: TranscriptFinalResult) => void,
    onError: (msg: string) => void,
    intervalMs = 3000,
    timeoutMs = 600_000,
  ): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    const poll = async () => {
      if (Date.now() > deadline) { onError('Extraction timed out.'); return; }
      try {
        const transcript = await extractionService.getTranscript(transcriptId);
        if (transcript.status === 'completed' && transcript.final_result) {
          onDone(transcript.final_result);
        } else if (transcript.status === 'error') {
          onError(transcript.error_message || 'Extraction failed.');
        } else {
          setTimeout(poll, intervalMs);
        }
      } catch {
        onError('Failed to fetch extraction status.');
      }
    };
    setTimeout(poll, intervalMs);
  },
};
